import axiosPublic from "api/axiosConfig";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleCheckBig, CircleX } from "lucide-react";

export default function RegistrationPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    name: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosPublic
      .get(`/auth/invite-token/${token}/check`)
      .then(() => setLoading(false))
      .catch(() => navigate("/"));
  }, [token, navigate]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "email":
        error = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ""
          : "Invalid email format";
        break;
      case "username":
        error =
          value.length >= 3 && value.length <= 20
            ? ""
            : "Username must be 3-20 characters";
        break;
      case "name":
        error =
          value.length >= 3 && value.length <= 40
            ? ""
            : "DJ name must be 3-40 characters";
        break;
      case "password":
        error =
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&_\-!"№#]{8,}$/.test(
            value
          )
            ? ""
            : "Password must have 8+ chars, one uppercase, one lowercase, and one number";
        break;
      case "confirmPassword":
        error = value === formData.password ? "" : "Passwords do not match";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Object.values(errors).some((err) => err)) return;

    try {
      await axiosPublic.post(`/dj/create/${token}`, formData);
      navigate("/login");
    } catch (err) {
      if (err.status === 409) {
        setErrors((prev) => ({
          ...prev,
          general: "Email or username already exists",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general: "Something went wrong, try later",
        }));
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
      <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-semibold mb-4">DJ Registration</h2>
        {errors.general && (
          <p className="text-red-500 mb-3">{errors.general}</p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {[
            {
              label: "Email",
              name: "email",
              type: "email",
              placeholder: "example@mail.com",
            },
            {
              label: "Username",
              name: "username",
              type: "text",
              placeholder: "Your username",
            },
            {
              label: "DJ Name",
              name: "name",
              type: "text",
              placeholder: "Your DJ name",
            },
            {
              label: "Password",
              name: "password",
              type: "password",
              placeholder: "Your password",
            },
            {
              label: "Confirm Password",
              name: "confirmPassword",
              type: "password",
              placeholder: "Confirm your password",
            },
          ].map(({ label, name, type, placeholder }) => (
            <div key={name}>
              <label className="block text-sm font-medium text-gray-700">
                {label}
              </label>
              <div className="relative">
                <input
                  type={type}
                  name={name}
                  placeholder={placeholder}
                  value={formData[name]}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors[name]
                      ? "border-red-500"
                      : formData[name]
                      ? "border-green-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {errors[name] && (
                  <CircleX className="w-5 h-5 text-red-500 absolute right-2 top-2.5" />
                )}
                {!errors[name] && formData[name] && (
                  <CircleCheckBig className="w-5 h-5 text-green-500 absolute right-2 top-2.5" />
                )}
              </div>
              {errors[name] && (
                <p className="text-red-500 text-xs mt-1">{errors[name]}</p>
              )}
            </div>
          ))}
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 rounded"
          >
            Create account
          </button>
        </form>
      </div>
    </div>
  );
}
